"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TabGroup = void 0;
function getAnchorName(a) {
    const href = a.href;
    const split = href.lastIndexOf("#");
    return split > -1 ? href.substring(split + 1) : "";
}
function getElementName(el) {
    return el.id ? `#${el.id}` : el.tagName;
}
class TabGroup {
    current = "";
    handles;
    listeners;
    constructor(container) {
        this.handles = new Map();
        this.listeners = new Map();
        this.themeInit();
        this.domInit(container);
    }
    addHandle(handle) {
        if (handle.name === "") {
            throw new Error("Tab name is required");
        }
        this.handles.set(handle.name, handle);
        if (this.current === "") {
            this.select(handle.name);
        }
        else {
            handle.deactivate();
        }
    }
    select(name) {
        if (name === this.current) {
            return;
        }
        const active = [];
        let match;
        for (const handle of this.handles.values()) {
            if (handle.name === name) {
                match = handle;
            }
            else if (handle.active) {
                active.push(handle);
            }
        }
        if (!match) {
            console.error("select tab %s: no match", name);
        }
        if (this.current !== "" && !active.length) {
            console.error("select tab %s: none active after init", name);
            return;
        }
        for (const handle of active) {
            handle.deactivate();
        }
        match.activate();
        const prevName = this.current;
        this.current = name;
        this.emit("change", { name: prevName });
    }
    addEventListener(name, fn) {
        if (!this.listeners.has(name)) {
            this.listeners.set(name, new Set([fn]));
        }
        else {
            this.listeners.get(name).add(fn);
        }
    }
    removeEventListener(name, fn) {
        this.listeners.get(name)?.delete(fn);
    }
    domInit(root) {
        const tabRow = root.querySelector(":scope > .tabs-select-row");
        if (!tabRow) {
            console.error("No .tabs-select-row element in %s", getElementName(root));
            return;
        }
        const regions = new Map();
        for (const region of root.querySelectorAll(":scope > .tab-region[id]")) {
            regions.set(region.id, region);
        }
        for (const tab of tabRow.querySelectorAll(":scope > .tab-select")) {
            const anchor = tab.querySelector("a[href^='#']");
            if (!(anchor && anchor instanceof HTMLAnchorElement)) {
                console.error("Tab missing/incorrect <a> element in %s", root.id);
                continue;
            }
            const name = getAnchorName(anchor);
            if (name.length > 0 && regions.has(name)) {
                this.addHandle(new TabHandle(name, tab, regions.get(name)));
                continue;
            }
            if (name.length === 0) {
                console.error("Tab has no name in %s", getElementName(root));
            }
            else {
                console.error("Tab missing related #%s.tab-region in %s", name, getElementName(root));
            }
        }
        tabRow.addEventListener("click", (ev) => {
            const e = ev.target;
            ev.preventDefault();
            if (!(e instanceof HTMLAnchorElement)) {
                return;
            }
            const name = getAnchorName(e);
            if (name && name.length > 0) {
                this.select(name);
            }
        });
    }
    themeInit() {
        if (typeof window.matchMedia !== "function") {
            return;
        }
        const mm = window.matchMedia("(prefers-color-scheme: dark)");
        if (typeof mm.addEventListener === "function") {
            const onChange = (_ev) => {
                if (this.current !== "") {
                    this.handles.get(this.current).update();
                }
            };
            mm.addEventListener("change", onChange);
        }
    }
    emit(name, data) {
        if (!this.listeners.has(name)) {
            return;
        }
        for (const fn of this.listeners.get(name).values()) {
            const ev = new CustomEvent(name, { detail: data });
            fn(ev);
        }
    }
}
exports.TabGroup = TabGroup;
class TabHandle {
    name;
    header;
    region;
    isActive = false;
    constructor(name, header, region) {
        this.name = name;
        this.header = header;
        this.region = region;
    }
    get active() {
        return this.isActive;
    }
    activate() {
        this.isActive = true;
        this.header.classList.add("active");
        this.region.classList.remove("hide");
        this.update();
    }
    deactivate() {
        this.isActive = false;
        this.header.classList.remove("active");
        this.region.classList.add("hide");
        if (this.header instanceof HTMLElement) {
            this.header.style.borderBottomColor = "transparent";
        }
    }
    update() {
        if (!(this.header instanceof HTMLElement)) {
            return;
        }
        const src = getComputedStyle(document.body);
        this.header.style.borderBottomColor = src.backgroundColor;
    }
}
